/**
 * WastePanel — JICA semi-aerobic landfill status.
 *
 * Thailand's first JICA-funded semi-aerobic landfill in Sikhio.
 * Shows capacity usage, daily intake, and remaining days.
 */

import type { FallbackTier } from "@sikhio/shared";
import { PanelHeader } from "./PanelHeader";

export interface WasteStatus {
  id: string;
  facilityName: string;
  facilityNameEn: string;
  technology: string;
  capacityTonnes: number;
  usedTonnes: number;
  pctUsed: number;
  dailyIntakeTonnes: number;
  remainingDays: number | null;
  alertLevel: "normal" | "watch" | "warning" | "critical";
  recyclingRatePct: number | null;
  pyrolysisActive: boolean;
  source: string;
  observedAt: string;
}

interface Props {
  data: WasteStatus | null;
  loading: boolean;
  ageMinutes?: number;
  fallbackTier?: FallbackTier;
}

const ALERT_COLOR: Record<string, string> = {
  normal:   "var(--good)",
  watch:    "var(--gold)",
  warning:  "var(--warn)",
  critical: "var(--bad)",
};

function Bar({ pct, alertLevel }: { pct: number; alertLevel: string }) {
  const col = ALERT_COLOR[alertLevel] ?? "var(--data)";
  return (
    <div style={{ height: 4, background: "var(--line)", position: "relative", marginTop: 4 }}>
      <div style={{ position: "absolute", left: 0, top: 0, height: "100%", width: `${Math.min(100, pct)}%`, background: col }} />
    </div>
  );
}

export function WastePanel({ data, loading, ageMinutes, fallbackTier }: Props) {
  const status = data;

  if (loading && !status) {
    return (
      <div className="col">
        <div className="eyebrow">ขยะ / JICA LANDFILL</div>
        <div className="skeleton" style={{ height: 12, marginTop: 8 }} />
      </div>
    );
  }
  if (!status) return null;

  const col = ALERT_COLOR[status.alertLevel] ?? "var(--data)";

  return (
    <div className="col" style={{ gap: 4 }}>
      <PanelHeader
        title="ขยะ "
        fallbackTier={fallbackTier}
        ageMinutes={ageMinutes}
      />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <span className="mono" style={{ fontSize: "var(--size-h3)", color: col, fontWeight: 700 }}>
          {status.pctUsed}%
        </span>
        <span className="caption" style={{ color: "var(--text-3)" }}>
          ใช้แล้ว {status.usedTonnes.toLocaleString()} / {status.capacityTonnes.toLocaleString()} ตัน
        </span>
      </div>
      <Bar pct={status.pctUsed} alertLevel={status.alertLevel} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 12px", marginTop: 6 }}>
        <span className="caption">รับขยะ/วัน</span>
        <span className="mono caption" style={{ textAlign: "right" }}>{status.dailyIntakeTonnes} ตัน/วัน</span>
        {status.remainingDays != null && (
          <>
            <span className="caption">คาดว่าจะเต็มใน</span>
            <span className="mono caption" style={{ textAlign: "right", color: status.remainingDays < 365 ? "var(--warn)" : "var(--text-1)" }}>
              ~{status.remainingDays} วัน
            </span>
          </>
        )}
        {status.recyclingRatePct != null && (
          <>
            <span className="caption">อัตราการรีไซเคิล</span>
            <span className="mono caption" style={{ textAlign: "right" }}>{status.recyclingRatePct}%</span>
          </>
        )}
      </div>
      <div className="caption" style={{ marginTop: 4, color: "var(--text-3)" }}>
        {status.technology} · ต้นแบบแห่งแรกในไทย (JICA)
      </div>
    </div>
  );
}
